<template>
  <section class="p-2 bg-light rounded shadow border-start-blue mb-2">
    <h5 class="fw-bold text-primary mb-2">Configuração do Departamento</h5>

    <b-row>
      <b-col cols="6">
        <validation-provider #default="{ errors }" name="Departamento" rules="required">
          <b-form-group
            label="Departamento Regional do SESI*"
            label-for="departamento"
            :state="errors.length > 0 || form.errors.departamento ? false : null"
          >
            <v-select
              id="departamento"
              :value="form.departamento"
              @input="$emit('update:departamento', $event)"
              variant="primary"
              :options="optionsDepartamentos"
              label="descricao"
              placeholder="Selecione um departamento"
              class="bg-white"
            >
              <span slot="no-options">Nenhuma opção selecionável.</span>
            </v-select>
            <small v-if="errors[0]" class="text-danger d-block">Este campo é de preenchimento obrigatório.</small>
            <small v-if="form.errors.departamento" class="text-danger">{{ form.errors.departamento }}</small>
          </b-form-group>
        </validation-provider>
      </b-col>

      <b-col cols="6" v-if="form.departamento">
        <validation-provider #default="{ errors }" name="Identificador do Solicitante" rules="required|integer">
          <b-form-group
            label="Identificador do Solicitante (Naming System)*"
            label-for="id-solicitante"
            :state="errors.length > 0 || form.errors.idSolicitante ? false : null"
            description="Número fornecido pela RNDS ao aprovar a solicitação de acesso. Portal de Serviços > Gerenciar Credenciais."
          >
            <b-form-input
              id="id-solicitante"
              type="text"
              :value="form.idSolicitante"
              @input="$emit('update:idSolicitante', $event)"
              @keypress="onlyNumber"
              :state="errors.length === 0 && !form.errors.idSolicitante ? null : false"
              placeholder="Ex: 51074"
              maxlength="10"
              :disabled="!permiteGerenciar"
            />
            <small v-if="errors[0]" class="text-danger d-block">Este campo é de preenchimento obrigatório.</small>
            <small v-if="form.errors.idSolicitante" class="text-danger">{{ form.errors.idSolicitante }}</small>
          </b-form-group>
        </validation-provider>
      </b-col>
    </b-row>

    <b-row>
      <b-col v-if="form.departamento && form.nomeCertificado && !localAdicionarCertificado">
        <b-form-group label="Certificado Digital do DR*" label-for="certificado-info">
          <div id="certificado-info" class="d-flex align-items-center mt-1">
            <span class="mr-1">{{ form.nomeCertificado }}</span>
            <feather-icon
              v-if="permiteGerenciar"
              icon="EditIcon"
              v-b-tooltip.hover.html
              size="14"
              class="cursor-pointer"
              title="Editar certificado"
              @click="localAdicionarCertificado = true"
            />
          </div>
        </b-form-group>
      </b-col>

      <b-col v-if="form.departamento && localAdicionarCertificado">
        <validation-provider #default="{ errors }" name="Certificado Digital" rules="required">
          <b-form-group
            label="Certificado Digital do DR*"
            label-for="certificado"
            :state="errors.length > 0 || form.errors.certificado ? false : null"
          >
            <div class="d-flex align-items-center">
              <b-form-file
                id="certificado"
                :value="form.certificado"
                @input="$emit('update:certificado', $event)"
                :state="errors.length === 0 && !form.errors.certificado ? null : false"
                placeholder="Escolha o arquivo .pfx"
                browse-text="Selecionar"
                accept=".pfx"
                class="flex-grow-1"
                :disabled="!permiteGerenciar"
              />
              <feather-icon
                icon="XIcon"
                size="20"
                class="ml-1 cursor-pointer"
                title="Cancelar edição de certificado"
                v-b-tooltip.hover.html
                v-if="form.nomeCertificado"
                @click="localAdicionarCertificado = false"
              />
            </div>
            <small v-if="errors[0]" class="text-danger d-block">Este campo é de preenchimento obrigatório.</small>
            <small v-if="form.errors.certificado" class="text-danger">{{ form.errors.certificado }}</small>
          </b-form-group>
        </validation-provider>
      </b-col>
    </b-row>

    <b-row v-if="form.departamento && localAdicionarCertificado">
      <b-col>
        <validation-provider #default="{ errors }" name="Senha do Certificado" rules="required">
          <b-form-group
            label="Chave de Acesso do Certificado*"
            label-for="chave-certificado"
            :state="errors.length > 0 || form.errors.chaveAcesso ? false : null"
          >
            <b-form-input
              id="chave-certificado"
              type="password"
              :value="form.chaveAcesso"
              @input="$emit('update:chaveAcesso', $event)"
              :state="errors.length === 0 && !form.errors.chaveAcesso ? null : false"
              placeholder="Digite a chave de acesso do certificado"
              autocomplete="new-password"
              :disabled="!permiteGerenciar"
            />
            <small v-if="errors[0]" class="text-danger d-block">Este campo é de preenchimento obrigatório.</small>
            <small v-if="form.errors.chave" class="text-danger">{{ form.errors.chave }}</small>
          </b-form-group>
        </validation-provider>
      </b-col>
    </b-row>
  </section>
</template>

<script>
import { BRow, BCol, BFormGroup, BFormFile, BFormInput } from 'bootstrap-vue';
import { ValidationProvider } from 'vee-validate';
import {required, integer} from '@validations';
import vSelect from 'vue-select';
import helpers from '@/helpers';

export default {
  name: 'ConfiguracaoDepartamento',
  components: { BRow, BCol, BFormGroup, BFormFile, BFormInput, ValidationProvider, vSelect },
  props: {
    form: { type: Object, required: true },
    optionsDepartamentos: { type: Array, default: () => [] },
    permiteGerenciar: { type: Boolean, default: false },
    adicionarCertificado: { type: Boolean, default: false }
  },
  data() {
    return {
      localAdicionarCertificado: this.adicionarCertificado,
      required,
      integer
    };
  },
  watch: {
    adicionarCertificado(val) { this.localAdicionarCertificado = val; },
    localAdicionarCertificado(val) { this.$emit('update:adicionarCertificado', val); }
  },
  methods: {
    onlyNumber(event) {
      if (!helpers.keyIsNumber(event)) {
        event.preventDefault();
      }
    }
  }
};
</script>
